import { type ChangeEvent, forwardRef, useRef } from "react";
import TextField, { type OutlinedTextFieldProps, type TextFieldProps } from "@mui/material/TextField";
import { InputAdornment } from "@mui/material";
import IconComponent from "@CubeComponents/icons";

type CubeTextFieldProps = Omit<OutlinedTextFieldProps, "variant"> & {
  variant?: "outlined" | "filled" | "standard";
  showClearButton?: boolean;
  height?: string;
  width?: string;
  // KNOWN BUG, preserved verbatim: Syncfusion-era callers pass `isDisabled` instead of
  // `disabled`; it is spread onto the DOM input and has no effect. Declared so those call
  // sites type-check without reshaping their props.
  isDisabled?: boolean;
  // KNOWN BUG, preserved verbatim: same story as `isDisabled` - `isError` is a Syncfusion
  // prop name that MUI ignores and spreads onto the DOM input.
  isError?: boolean;
};

/**
 * The plain-object branch of MUI's `SxProps<Theme>` union. `sx` is declared as that union (object |
 * callback | array), so the two nested selector reads below cannot index it directly. Every caller
 * of this component passes the object form. Leaves are only ever spread, never read as primitives,
 * which is why they can be typed as nested selector objects.
 */
type SxSelectorObject = { [selector: string]: SxSelectorObject | undefined };

/**
 * True for the plain-object branch of `SxProps<Theme>`. The callback and array branches carry no
 * selector keys, so they read as "no overrides" — which is exactly what indexing them produced
 * before. Declared over `unknown` because the object branch is not assignable to the recursive
 * index signature above, so no annotation on `props.sx` could express the same narrowing.
 */
function isSxSelectorObject(value: unknown): value is SxSelectorObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const TextFieldComponent = forwardRef<HTMLInputElement, CubeTextFieldProps>((props, ref) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const { height = "32px", sx = {}, multiline = false, showClearButton, ...restProps } = props;

  // The caller's own nested-selector overrides, merged into the two rules below. `sx` is optional,
  // so this stays `undefined` when nothing was passed and the reads below remain optional-chained.
  const sxOverrides = isSxSelectorObject(props.sx) ? props.sx : undefined;

  const setRefs = (element: HTMLInputElement | null) => {
    internalRef.current = element;
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = element;
    }
  };

  // Always a fresh object, never the caller's own — hence `NonNullable`, which is what lets the
  // clear-button branch below assign `input` without re-asserting that it exists.
  const slotProps: NonNullable<TextFieldProps["slotProps"]> = { ...restProps.slotProps };
  // `slotProps.input` is either a props object or an ownerState callback; only the object form can
  // carry an adornment the caller already supplied, and only it is meaningful to spread into the
  // replacement below. MUI types that branch as a wide `ComponentPropsWithRef` union, so the
  // adornment is read through `in` rather than assumed present on every member of it.
  const callerInputSlot = typeof slotProps.input === "function" ? undefined : slotProps.input;
  const callerEndAdornment =
    callerInputSlot && "endAdornment" in callerInputSlot ? callerInputSlot.endAdornment : undefined;
  if (showClearButton && !callerEndAdornment) {
    slotProps.input = {
      ...callerInputSlot,
      endAdornment: (
        <InputAdornment position="end">
          <IconComponent
            name="cross"
            onClick={() => {
              if (internalRef.current) {
                internalRef.current.value = "";
                // React's SyntheticEvent cannot be constructed outside React's own event system,
                // so the clear button hands `onChange` the real input element as `target` right
                // after blanking its value — which is what `e.target.value` / `e.target.name`
                // consumers read.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above; a real ChangeEvent is not constructible here
                restProps.onChange?.({ target: internalRef.current } as ChangeEvent<HTMLInputElement>);
              }
            }}
            width="1.125rem"
            height="1.125rem"
            className="cursor-pointer"
          />
        </InputAdornment>
      ),
    };
  }

  return (
    <TextField
      inputRef={setRefs}
      multiline={multiline}
      sx={{
        width: props.width ?? "100%",
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        ...sx,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "var(--surface-white)",
          ...sxOverrides?.["& .MuiOutlinedInput-root"],
          minHeight: height,
          borderColor: "#666A84",
          paddingTop: "0 !important",
          paddingInlineStart: "0 !important",
          paddingBottom: "0 !important",
        },
        "& .MuiInputBase-input::placeholder": {
          color: "#666A84",
          opacity: 1,
        },

        "& .MuiOutlinedInput-root:hover .MuiInputBase-input::placeholder": {
          color: "#192048 !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#666A84",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#6BBBE3",
          borderWidth: "1px",
          ...sxOverrides?.["& .MuiOutlinedInput-root"]?.["&.Mui-focused fieldset"],
        },
        "& .MuiOutlinedInput-root.Mui-disabled": {
          backgroundColor: "var(--light-blue-tag)",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
          backgroundColor: "var(--surface-white)",
          padding: "0.3125rem 0.625rem !important",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-input": {
          backgroundColor: "inherit",
        },
        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
      }}
      {...restProps}
      slotProps={slotProps}
    />
  );
});

TextFieldComponent.displayName = "TextFieldComponent";

export default TextFieldComponent;
